import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, FREE_SHIPPING_THRESHOLD, SHIPPING_METHOD, SHIPPING_TIME } from '../data/mock';
import { Truck, Clock, Package } from 'lucide-react';

const ShippingPage = () => {
  useEffect(() => {
    document.title = 'Shipping Information | EC Pedal Works';
  }, []);

  const isAllFree = FREE_SHIPPING_THRESHOLD === 0;

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />

      <main className="">
        {/* Banner Section */}
        <section className="relative bg-gray-50 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-full text-orange-600 text-xs font-semibold mb-4">
              <Truck className="w-3.5 h-3.5" />
              DELIVERY INFO
            </div>
            <h1 className="font-display text-4xl md:text-5xl text-gray-900 font-extrabold mb-3">
              Shipping Information
            </h1>
            <p className="text-gray-600 max-w-lg">
              Fast, reliable shipping across the United States
            </p>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Shipping Highlights */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-orange-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Truck className="w-7 h-7 text-orange-600" />
              </div>
              <h3 className="font-display font-bold text-gray-900 text-lg mb-2">Free USA Shipping</h3>
              <p className="text-gray-500 text-sm">
                {isAllFree ? 'On every order, no minimum' : `On orders over $${FREE_SHIPPING_THRESHOLD}`}
              </p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-orange-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Clock className="w-7 h-7 text-orange-600" />
              </div>
              <h3 className="font-display font-bold text-gray-900 text-lg mb-2">{SHIPPING_TIME}</h3>
              <p className="text-gray-500 text-sm">Standard delivery time</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 text-center">
              <div className="w-14 h-14 bg-orange-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Package className="w-7 h-7 text-orange-600" />
              </div>
              <h3 className="font-display font-bold text-gray-900 text-lg mb-2">Order Tracking</h3>
              <p className="text-gray-500 text-sm">Track your shipment via {SHIPPING_METHOD}</p>
            </div>
          </div>

          {/* Shipping Details */}
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 md:p-12">
            <h2 className="font-display text-2xl font-bold text-gray-900 mb-6">Shipping Policy</h2>

            <div className="space-y-8">
              <div>
                <h3 className="font-display font-bold text-gray-900 text-lg mb-3">Shipping Rates</h3>
                <div className="bg-gray-100 rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left p-4 text-gray-500 font-semibold">Order Total</th>
                        <th className="text-left p-4 text-gray-500 font-semibold">Shipping Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isAllFree ? (
                        <tr>
                          <td className="p-4 text-gray-900">All U.S. orders</td>
                          <td className="p-4 text-orange-600 font-semibold">FREE</td>
                        </tr>
                      ) : (
                        <>
                          <tr className="border-b border-gray-200">
                            <td className="p-4 text-gray-900">Under ${FREE_SHIPPING_THRESHOLD}</td>
                            <td className="p-4 text-gray-900">Flat rate (shown at checkout)</td>
                          </tr>
                          <tr>
                            <td className="p-4 text-gray-900">${FREE_SHIPPING_THRESHOLD} and above</td>
                            <td className="p-4 text-orange-600 font-semibold">FREE</td>
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="font-display font-bold text-gray-900 text-lg mb-3">Order Processing</h3>
                <p className="text-gray-600 mb-4">
                  Orders are processed within 1–2 business days (excluding weekends and U.S. holidays). You will receive a tracking number by email once your order has shipped.
                </p>
                <ul className="text-gray-600 space-y-2 list-disc list-inside">
                  <li>Orders placed before 2 PM EST are typically processed the same business day</li>
                  <li>Orders placed after 2 PM EST are processed the next business day</li>
                  <li>No order processing or deliveries on weekends or U.S. holidays</li>
                </ul>
              </div>

              <div>
                <h3 className="font-display font-bold text-gray-900 text-lg mb-3">Delivery Times</h3>
                <p className="text-gray-600">
                  Standard delivery takes approximately <span className="font-semibold text-gray-900">{SHIPPING_TIME}</span> from the date of shipment. Delivery times may vary based on your location and carrier conditions.
                </p>
              </div>

              <div>
                <h3 className="font-display font-bold text-gray-900 text-lg mb-3">Shipping Carrier</h3>
                <p className="text-gray-600">
                  We ship all orders via {SHIPPING_METHOD} (United States Postal Service). A tracking number will be emailed to you as soon as your order ships.
                </p>
              </div>

              <div>
                <h3 className="font-display font-bold text-gray-900 text-lg mb-3">Shipping Restrictions</h3>
                <p className="text-gray-600">
                  We currently only ship within the United States. We do not ship to P.O. boxes, APO/FPO addresses, or international destinations at this time. Risk of loss and title pass to you upon delivery to the carrier.
                </p>
              </div>

              <div>
                <h3 className="font-display font-bold text-gray-900 text-lg mb-3">Questions?</h3>
                <p className="text-gray-600 mb-4">
                  If you have any questions about shipping, please contact our support team:
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href={`mailto:${CONTACT_INFO.email}`}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-athletic-500 text-gray-900 font-semibold rounded-xl hover:bg-athletic-400 transition-colors"
                  >
                    {CONTACT_INFO.email}
                  </a>
                  <a
                    href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-900 font-semibold rounded-xl hover:bg-gray-200 transition-colors border border-gray-200"
                  >
                    {CONTACT_INFO.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ShippingPage;
